/**
 * Format Document — editor-agnostic. Prettier (HTML with its inline CSS / JS,
 * JS, TS, CSS / SCSS / Less, JSON, Markdown, YAML), sql-formatter (SQL, as
 * papan.db's SQLite, keeping `:arg` / `@context` parameters intact) and Ruff
 * (Python, #987 — its WebAssembly build, ~3.6 MB gzipped). Each loads on the
 * first format of its language only — nothing is added to the editor's own
 * bundle, and the browser caches it after.
 *
 * The result is the new text, or a refusal the editor shows: an unsupported
 * language, or a parse error (nothing is changed then — a formatter that can't
 * parse the code must never rewrite it).
 */

import { EMPTY_FORMAT_CONFIG, type FormatConfig } from './format-config.js'

export type FormatResult =
  /** `ignoredPlugins`: plugins the project's `.prettierrc` lists that aren't on Papan's vetted list. */
  | { ok: true; text: string; ignoredPlugins?: string[]; unreadableConfig?: string }
  | { ok: false; reason: 'unsupported' }
  /** The formatter itself failed — never the code's fault: it took too long, its worker stopped, or it didn't load. */
  | { ok: false; reason: 'failed'; cause: 'timeout' | 'crashed' | 'load' }
  /** A file type a vetted plugin formats, but the project hasn't turned that plugin on. */
  | { ok: false; reason: 'needsPlugin'; plugin: string }
  | { ok: false; reason: 'parse'; message: string }
  /** The text is plainly another language than the file's (an HTML page pasted into a `.js` file). */
  | { ok: false; reason: 'mismatch'; looksLike: 'HTML' }

/** highlight.js language names (the editor's `language` prop) → Prettier parser. */
const PRETTIER_PARSER: Record<string, string> = {
  html: 'html',
  javascript: 'babel',
  typescript: 'typescript',
  css: 'css',
  scss: 'scss',
  less: 'less',
  json: 'json',
  markdown: 'markdown',
  yaml: 'yaml',
}

/**
 * #987 — Prettier plugins Papan has checked and ships (the "vetted list"): each
 * runs only when the project lists it in its `.prettierrc` `plugins`, like in
 * VS Code. Both are the Prettier team's own, pure JavaScript, and load on first
 * use. Anything else a `.prettierrc` lists is ignored, with a note — no
 * third-party code from a project runs in the editor.
 */
const VETTED_PLUGINS: Record<string, { load: () => Promise<unknown>; parsers: Record<string, string> }> = {
  '@prettier/plugin-xml': { load: () => import('@prettier/plugin-xml'), parsers: { xml: 'xml', svg: 'xml', xsd: 'xml', xsl: 'xml', xslt: 'xml', plist: 'xml' } },
  '@prettier/plugin-php': { load: () => import('@prettier/plugin-php/standalone'), parsers: { php: 'php' } },
}
/** The newest PHP @prettier/plugin-php 0.26 knows — used when a project gives no number. */
const PHP_NEWEST = '8.5'
const PLUGIN_FOR_EXT: Record<string, string> = Object.fromEntries(
  Object.entries(VETTED_PLUGINS).flatMap(([name, p]) => Object.keys(p.parsers).map((ext) => [ext, name])),
)

/** File extensions the text editors open with a formattable language (their highlight maps), plus the vetted plugins'. */
const FORMATTABLE_EXT = new Set(['html', 'htm', 'js', 'mjs', 'jsx', 'ts', 'tsx', 'css', 'json', 'md', 'yaml', 'yml', 'sql', 'py', ...Object.keys(PLUGIN_FOR_EXT)])

/** Whether a file of this name can be formatted — for menus that know the file, not the editor's language. */
export function canFormatFile(name: string | undefined): boolean {
  const ext = name?.split('.').pop()?.toLowerCase()
  return !!ext && name!.includes('.') && FORMATTABLE_EXT.has(ext)
}

/** Whether `language` can be formatted (for menus: don't offer what can't run). */
export function canFormat(language: string | undefined): boolean {
  return !!language && (language === 'sql' || language === 'python' || language in PRETTIER_PARSER)
}

/**
 * The file's own JS style, so formatting doesn't flip every quote and semicolon
 * of code that was written the other way: single quotes when they outnumber
 * double ones, no semicolons when fewer than a third of statement-ending lines
 * carry one.
 */
export function detectJsStyle(text: string): { singleQuote: boolean; semi: boolean } {
  const single = (text.match(/'[^'\n]*'/g) ?? []).length
  const double = (text.match(/"[^"\n]*"/g) ?? []).length
  const lines = text.split('\n').map((l) => l.trim()).filter((l) => l && !l.startsWith('//') && !l.startsWith('*') && !l.startsWith('<'))
  const ends = lines.filter((l) => /[\w)\]'"`]$|;$/.test(l))
  const withSemi = ends.filter((l) => l.endsWith(';')).length
  return { singleQuote: single > double, semi: ends.length === 0 || withSemi / ends.length >= 1 / 3 }
}

async function prettierPlugins(parser: string) {
  const load = {
    estree: () => import('prettier/plugins/estree'),
    babel: () => import('prettier/plugins/babel'),
    typescript: () => import('prettier/plugins/typescript'),
    postcss: () => import('prettier/plugins/postcss'),
    html: () => import('prettier/plugins/html'),
    markdown: () => import('prettier/plugins/markdown'),
    yaml: () => import('prettier/plugins/yaml'),
  }
  const names: Array<keyof typeof load> =
    parser === 'html' ? ['html', 'babel', 'estree', 'postcss']
      : parser === 'babel' || parser === 'json' ? ['babel', 'estree']
        : parser === 'typescript' ? ['typescript', 'estree']
          : parser === 'css' || parser === 'scss' || parser === 'less' ? ['postcss']
            : parser === 'markdown' ? ['markdown', 'babel', 'estree', 'postcss']
              : ['yaml']
  return Promise.all(names.map((n) => load[n]()))
}

/** A whole HTML page or fragment: it opens with a doctype or one of the document's own tags. */
const LOOKS_LIKE_HTML = /^\s*(<!--[\s\S]*?-->\s*)*<(!doctype\s+html|html[\s>]|head[\s>]|body[\s>])/i

/** Prettier's error message without its code frame (the first line names the problem and where). */
function shortMessage(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err)
  return msg.split('\n')[0]!.trim()
}

type RuffModule = typeof import('@astral-sh/ruff-wasm-web')
let ruffReady: Promise<RuffModule> | null = null

/** How Ruff's WebAssembly is fetched — the bundled asset; tests read it from disk. */
let loadRuffWasm = async (): Promise<BufferSource | URL | string> =>
  (await import('@astral-sh/ruff-wasm-web/ruff_wasm_bg.wasm?url')).default
/** Tests only: where the wasm bytes come from (no bundler, no fetch of a file URL). */
export function setRuffWasmLoader(load: () => Promise<BufferSource | URL | string>) {
  loadRuffWasm = load
  ruffReady = null
}

function ruff(): Promise<RuffModule> {
  ruffReady ??= (async () => {
    const mod = await import('@astral-sh/ruff-wasm-web')
    await mod.default({ module_or_path: await loadRuffWasm() })
    return mod
  })()
  ruffReady.catch(() => { ruffReady = null })
  return ruffReady
}

/** Ruff names a syntax error by byte range ("at byte range 4..5"); say the line instead. */
function ruffMessage(err: unknown, text: string): string {
  const msg = shortMessage(err)
  const m = /^(.*?) at byte range (\d+)\.\.\d+$/.exec(msg)
  if (!m) return msg
  const before = new TextDecoder().decode(new TextEncoder().encode(text).slice(0, Number(m[2])))
  return `${m[1]} (line ${before.split('\n').length})`
}

async function formatPython(text: string, config: FormatConfig): Promise<FormatResult> {
  let mod: RuffModule
  try {
    mod = await ruff()
  } catch (err) {
    console.warn('[format] the Python formatter did not load', err)
    return { ok: false, reason: 'failed', cause: 'load' }
  }
  try {
    // The project's Ruff settings when it has them; else keep the file's own quotes, as detectJsStyle does for JS.
    const settings = clampRuff(config.ruff) ?? { 'line-length': 88, format: { 'quote-style': 'preserve' } }
    const ws = new mod.Workspace(settings, mod.PositionEncoding.Utf16)
    try {
      return { ok: true, text: ws.format(text) }
    } finally {
      ws.free()
    }
  } catch (err) {
    return { ok: false, reason: 'parse', message: ruffMessage(err, text) }
  }
}

/** A project's widths, kept sane — a `.prettierrc` `tabWidth: 1e8` must not make the formatter run away. Later sources win. */
function clampWidths(...sources: Array<Record<string, unknown> | null | undefined>): Record<string, number> {
  const out: Record<string, number> = {}
  const clamp = (v: unknown, lo: number, hi: number) => (typeof v === 'number' && Number.isFinite(v) ? Math.min(hi, Math.max(lo, Math.round(v))) : undefined)
  for (const src of sources) {
    const tab = clamp(src?.tabWidth, 1, 16)
    const print = clamp(src?.printWidth, 20, 1000)
    if (tab !== undefined) out.tabWidth = tab
    if (print !== undefined) out.printWidth = print
  }
  return out
}

/** The project's Ruff settings, widths clamped (Ruff's own limits) and line endings forced to LF (see endOfLine above). */
function clampRuff(ruff: Record<string, unknown> | null): Record<string, unknown> | null {
  if (!ruff) return null
  const out: Record<string, unknown> = { ...ruff }
  const ll = ruff['line-length']
  if (typeof ll === 'number') out['line-length'] = Math.min(320, Math.max(1, Math.round(ll)))
  const iw = ruff['indent-width']
  if (typeof iw === 'number') out['indent-width'] = Math.min(16, Math.max(1, Math.round(iw)))
  const format = ruff.format && typeof ruff.format === 'object' ? { ...(ruff.format as Record<string, unknown>) } : {}
  format['line-ending'] = 'lf'
  out.format = format
  return out
}

export interface FormatOptions {
  /** The file's name — picks a vetted plugin's parser for a type the editor has no language for. */
  fileName?: string
  /** The project's own settings (project-config.ts). */
  config?: FormatConfig
}

function pluginNames(prettier: Record<string, unknown> | null): string[] {
  const list = prettier?.plugins
  return Array.isArray(list) ? list.filter((p): p is string => typeof p === 'string') : []
}

export async function formatDocument(text: string, language: string | undefined, opts: FormatOptions = {}): Promise<FormatResult> {
  const config = opts.config ?? EMPTY_FORMAT_CONFIG
  if (language === 'python') return formatPython(text, config)
  if (language === 'sql') {
    let format: typeof import('sql-formatter').format
    try {
      ({ format } = await import('sql-formatter'))
    } catch (err) {
      console.warn('[format] sql-formatter did not load', err)
      return { ok: false, reason: 'failed', cause: 'load' }
    }
    try {
      const out = format(text, {
        language: 'sqlite',
        // papan.db binds caller args as `:name` and server context as `@name`.
        paramTypes: { named: [':', '@'] },
        keywordCase: 'preserve',
        tabWidth: clampWidths(config.editorconfig).tabWidth ?? 2,
        useTabs: config.editorconfig?.useTabs ?? false,
      })
      return { ok: true, text: text.endsWith('\n') && !out.endsWith('\n') ? out + '\n' : out }
    } catch (err) {
      return { ok: false, reason: 'parse', message: shortMessage(err) }
    }
  }
  const listed = pluginNames(config.prettier)
  const ignoredPlugins = listed.filter((p) => !(p in VETTED_PLUGINS))
  const ext = opts.fileName?.includes('.') ? opts.fileName.split('.').pop()!.toLowerCase() : ''
  let parser = language ? PRETTIER_PARSER[language] : undefined
  let pluginName: string | undefined
  if (!parser && PLUGIN_FOR_EXT[ext]) {
    pluginName = PLUGIN_FOR_EXT[ext]!
    if (!listed.includes(pluginName)) return { ok: false, reason: 'needsPlugin', plugin: pluginName }
    parser = VETTED_PLUGINS[pluginName]!.parsers[ext]
  }
  if (!parser) return { ok: false, reason: 'unsupported' }
  // The project's `.prettierrc` (Prettier's defaults beneath it); else Papan's defaults and the file's own JS style.
  // `.editorconfig` sits under either, as in Prettier. `plugins` are resolved above: only vetted ones load.
  const { plugins: _listed, ...projectOptions } = config.prettier ?? {}
  const base = config.prettier ? {} : { printWidth: 100, tabWidth: 2, ...detectJsStyle(text) }
  let loaded: [typeof import('prettier/standalone'), unknown[], unknown]
  try {
    loaded = await Promise.all([
      import('prettier/standalone'),
      pluginName ? Promise.resolve([]) : prettierPlugins(parser),
      pluginName ? VETTED_PLUGINS[pluginName]!.load() : Promise.resolve(null),
    ])
  } catch (err) {
    // A chunk that didn't download is the formatter failing to load — not the code failing to parse.
    console.warn('[format] the formatter did not load', err)
    return { ok: false, reason: 'failed', cause: 'load' }
  }
  try {
    const [{ format }, builtins, extra] = loaded
    const plugins = [...builtins, ...(extra ? [((extra as { default?: unknown }).default ?? extra)] : [])]
    const options: Record<string, unknown> = { ...base, ...config.editorconfig, ...projectOptions, ...clampWidths(config.editorconfig, projectOptions) }
    // The editor's text is always LF (CodeMirror's), so the output must be too: a `\r` in a hunk
    // would become an extra line break. A project's crlf / cr setting can't be honoured in the editor.
    options.endOfLine = 'lf'
    // @prettier/plugin-php's "auto" / "composer" phpVersion reads composer.json from the
    // disk (process.cwd()), which a browser hasn't got: give it a version — the project's, else the newest.
    if (parser === 'php' && !/^\d+(\.\d+)?$/.test(String(options.phpVersion ?? ''))) options.phpVersion = PHP_NEWEST
    const out = await format(text, { ...options, parser, plugins: plugins as never })
    return {
      ok: true,
      text: out,
      ...(ignoredPlugins.length ? { ignoredPlugins } : {}),
      ...(config.unreadable ? { unreadableConfig: config.unreadable } : {}),
    }
  } catch (err) {
    // A script parser choking on a page is not a syntax error worth a line and column — say what happened.
    if (parser !== 'html' && parser !== 'markdown' && LOOKS_LIKE_HTML.test(text)) return { ok: false, reason: 'mismatch', looksLike: 'HTML' }
    return { ok: false, reason: 'parse', message: shortMessage(err) }
  }
}
